import {
  closeSpatialAggregationPointOptions,
  closeSpatialAggregationPointOptionsWhenReady,
  showImportNewSuccessToast
} from "@/components/onboarding/utils/bringYourOwnData";

import {
  BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID,
  BRING_YOUR_OWN_DATA_DATA_SETTINGS_TOUR_ID,
  BRING_YOUR_OWN_DATA_DATASET_ID,
  BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID,
  BRING_YOUR_OWN_DATA_IMPORT_SUCCESS_TOUR_ID,
  BRING_YOUR_OWN_DATA_PUBLISH_DATA_TOUR_ID,
  BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID
} from "./bring-your-own-data.mocks";
import { ONBOARDING_MODULE_METADATA } from "./moduleVersions";
import type { ModuleConfig } from "./types";

const workspaceRoute = "/";
const datasetManagementRoute = "/dataset-management";
const datasetDetailRoute = `${datasetManagementRoute}/${BRING_YOUR_OWN_DATA_DATASET_ID}`;

const buildYourOwnDataIntroImageSrc = `${import.meta.env.VITE_BASE_PATH || "/"}images/onboarding_build_your_own_data.png`;

// Onboarding config for the Bring Your Own Data walkthrough. During the tour,
// dataset-management screens use scoped fixtures from bring-your-own-data.mocks.
export const bringYourOwnDataConfig: ModuleConfig = {
  code: ONBOARDING_MODULE_METADATA.bringYourOwnData.code,
  version: ONBOARDING_MODULE_METADATA.bringYourOwnData.version,
  // Opens on the workspace so the first step can introduce the sidebar without
  // jumping straight to dataset management; the later steps route into it.
  route: workspaceRoute,
  intro: {
    title: "Bring Your Own Data",
    description:
      "Provide a simple, guided onboarding experience that helps new users understand the core steps in the platform.",
    image: {
      src: buildYourOwnDataIntroImageSrc,
      alt: "Bring Your Own Data onboarding"
    },
    actions: [
      {
        label: "Let's Start",
        type: "start"
      }
    ]
  },
  steps: [
    {
      id: "step-user-data-management",
      tourId: BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID,
      title: "User Data Management",
      description:
        "Access User Management to upload and manage your own datasets",
      // No route override → inherits the module's workspace route, so the first
      // step never redirects into dataset management. The sidebar mirrors this
      // tourId onto its container, so the spotlight covers the whole sidebar
      // (as in the workspace tour) while the Dataset Management icon — which
      // carries the same tourId — renders in its active state.
      tooltipConfig: {
        placement: "right-start",
        placementOffset: { crossAxis: 112 },
        arrowOffset: 80
      },
      targetPadding: 0
    },
    {
      id: "step-add-your-own-dataset",
      tourId: "bring-your-own-data-empty-state",
      // First step lives on the workspace route, so the dataset-management
      // steps now pin their route explicitly instead of inheriting it.
      route: datasetManagementRoute,
      title: "Upload Dataset",
      description:
        "Click Upload to import your file. You can upload  datasets to your workspace.",
      tooltipConfig: {
        placement: "top",
        placementOffset: {
          mainAxis: 48
        }
      },
      targetPadding: { top: 32, right: 64, bottom: 32, left: 64 }
    },
    {
      id: "step-choose-upload-file",
      tourId: "bring-your-own-data-upload-dropzone",
      route: datasetManagementRoute,
      title: "Select File",
      description:
        "We support CSV, GeoJSON, Shapefile, and Excel formats. Make sure your file includes location attributes.",
      tooltipConfig: {
        placement: "right"
      },
      // Match the dialog's `rounded-lg` (8px) corners so the spotlight hole
      // fits the card exactly instead of poking past its rounded corners.
      targetRadius: 8
    },
    {
      id: "step-import-new-success",
      tourId: BRING_YOUR_OWN_DATA_IMPORT_SUCCESS_TOUR_ID,
      route: datasetDetailRoute,
      title: "Upload Success & Parsing",
      description:
        "Your file is being processed. This may take a moment depending on file size. You'll be notified once it's ready",
      tooltipConfig: {
        placement: "bottom",
        placementOffset: {
          mainAxis: 32
        }
      },
      targetPadding: 8,
      onActivate: showImportNewSuccessToast
    },
    {
      id: "step-data-settings",
      tourId: BRING_YOUR_OWN_DATA_DATA_SETTINGS_TOUR_ID,
      route: datasetDetailRoute,
      title: "Data Settings Panel",
      description:
        "Configure your dataset settings. Your data can be pushed to the platform once the settings are complete",
      tooltipConfig: {
        placement: "bottom"
      },
      targetPadding: 8
    },
    {
      id: "step-data-settings-popup",
      tourId: BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID,
      route: datasetDetailRoute,
      title: "Country Selector",
      description: "Set the country your data belongs to.",
      // The detail page opens the Data Settings popup while this step is active
      // — no onActivate needed here.
      tooltipConfig: {
        placement: "right",
        placementOffset: {
          mainAxis: 16
        }
      },
      // Match the popup's `!rounded-xl` (12px) corners.
      targetRadius: 12
    },
    {
      id: "step-choose-spatial-aggregation-point",
      tourId: BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID,
      route: datasetDetailRoute,
      title: "Spatial Aggregation Selector",
      description:
        "Choose the spatial type based on the geometry attribute in your data. Point, Polygon, H3, Administrative Area, or Line. Then Save to confirm.",
      tooltipConfig: {
        placement: "top",
        placementOffset: {
          mainAxis: 16
        }
      },
      // Same target geometry as the spatial-aggregation step, but the menu is
      // closed here because Point is already selected.
      targetPadding: { top: -4, x: 8, bottom: 8 },
      targetRadius: 8,
      onActivate: closeSpatialAggregationPointOptionsWhenReady,
      onPrev: closeSpatialAggregationPointOptions
    },
    {
      id: "step-publish-data",
      tourId: BRING_YOUR_OWN_DATA_PUBLISH_DATA_TOUR_ID,
      route: datasetDetailRoute,
      title: "Publish Your Data",
      description:
        "Publish to make it available in your  My Organization data explore  for analysis and visualization.",
      tooltipConfig: {
        placement: "top"
      },
      targetPadding: 8,
      targetRadius: 12
    }
  ]
};
